import { Clock3 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  ErrorState,
  Field,
  Select,
  useToast,
} from '@/components/ui';
import { useBranch } from '@/features/branch/branch-context';
import { useNow } from '@/features/shell/use-now';
import { isApiError } from '@/lib/api';
import { useClockIn, useClockOut, useMyAttendance } from './api';
import styles from './AttendanceClockControl.module.css';

type ClockAction = 'clock-in' | 'clock-out';

function formatTime(value: string | null) {
  if (!value) return '—';
  return new Intl.DateTimeFormat('en-MY', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(value));
}

function elapsedMinutes(
  workedMinutes: number | null,
  clockedInAt: string | null,
  now: Date,
) {
  if (workedMinutes !== null) return Math.max(0, workedMinutes);
  if (!clockedInAt) return 0;
  return Math.max(
    0,
    Math.floor((now.getTime() - new Date(clockedInAt).getTime()) / 60_000),
  );
}

export function AttendanceClockControl() {
  const { t } = useTranslation();
  const { branches, selected } = useBranch();
  const now = useNow();
  const attendance = useMyAttendance();
  const clockIn = useClockIn();
  const clockOut = useClockOut();
  const toast = useToast();
  const [manualBranchId, setManualBranchId] = useState(
    (branches.length === 1 ? branches[0]?.id : '') ?? '',
  );
  const [drawerAction, setDrawerAction] = useState<ClockAction | null>(null);
  const [requiredReasonAction, setRequiredReasonAction] =
    useState<ClockAction | null>(null);
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState(false);

  if (attendance.isLoading || attendance.error) return null;

  const branchId = selected?.id ?? manualBranchId;
  const today = attendance.data?.today;
  const isClockedIn = today?.state === 'clocked_in';
  const isComplete = today?.state === 'complete';
  const reasonRequired =
    drawerAction === requiredReasonAction ||
    (drawerAction === 'clock-in'
      ? attendance.data?.clockInReasonRequired
      : attendance.data?.clockOutReasonRequired);
  const activeMutation = drawerAction === 'clock-out' ? clockOut : clockIn;
  const actionError = activeMutation.error;
  const duration = elapsedMinutes(
    today?.workedMinutes ?? null,
    today?.clockedInAt ?? null,
    now,
  );

  const closeDrawer = () => {
    setDrawerAction(null);
    setRequiredReasonAction(null);
    setReason('');
    setReasonError(false);
    clockIn.reset();
    clockOut.reset();
  };

  const handleKnownError = (action: ClockAction, error: unknown) => {
    if (!isApiError(error)) return;
    if (
      error.code === 'LATE_REASON_REQUIRED' ||
      error.code === 'EARLY_REASON_REQUIRED'
    ) {
      setRequiredReasonAction(action);
      setDrawerAction(action);
      return;
    }
    if (
      error.code === 'ALREADY_CLOCKED_IN' ||
      error.code === 'NOT_CLOCKED_IN'
    ) {
      void attendance.refetch();
      setDrawerAction(null);
      toast.show({
        tone: 'error',
        title: t('attendance.stateChanged'),
        body: error.message,
        error,
        onRetry: () => void attendance.refetch(),
      });
    }
  };

  const act = (action: ClockAction, actionReason?: string) => {
    const mutation = action === 'clock-in' ? clockIn : clockOut;
    mutation.mutate(
      { branchId, reason: actionReason },
      {
        onSuccess: () => {
          closeDrawer();
          toast.show({
            tone: 'success',
            title: t(
              action === 'clock-in'
                ? 'attendance.clockedIn'
                : 'attendance.clockedOut',
            ),
          });
        },
        onError: (error) => handleKnownError(action, error),
      },
    );
  };

  const beginClockIn = () => {
    clockIn.reset();
    if (attendance.data?.clockInReasonRequired || !branchId) {
      setDrawerAction('clock-in');
    } else {
      act('clock-in');
    }
  };

  const submitDrawer = () => {
    if (!drawerAction) return;
    const cleanReason = reason.trim();
    setReasonError(Boolean(reasonRequired && !cleanReason));
    if (!branchId || (reasonRequired && !cleanReason)) return;
    act(drawerAction, cleanReason || undefined);
  };

  if (isComplete) return null;

  return (
    <>
      <Button
        className={styles.control}
        variant={isClockedIn ? 'soft' : 'primary'}
        size="sm"
        icon={<Clock3 size={15} />}
        onClick={() =>
          isClockedIn ? setDrawerAction('clock-out') : beginClockIn()
        }
        loading={clockIn.isPending && drawerAction === null}
      >
        {isClockedIn
          ? t('attendance.topbarSince', {
              time: formatTime(today?.clockedInAt ?? null),
            })
          : t('attendance.clockIn')}
      </Button>

      <Dialog
        open={drawerAction !== null}
        onClose={closeDrawer}
        title={
          drawerAction === 'clock-out'
            ? t('attendance.clockOutTitle')
            : t('attendance.lateReasonTitle')
        }
        footer={
          <>
            <Button onClick={closeDrawer}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              onClick={submitDrawer}
              loading={activeMutation.isPending}
              disabled={!branchId}
            >
              {t(
                drawerAction === 'clock-out'
                  ? 'attendance.clockOut'
                  : 'attendance.clockIn',
              )}
            </Button>
          </>
        }
      >
        {drawerAction === 'clock-out' && (
          <div className={styles.duration}>
            <span>{t('attendance.totalWorked')}</span>
            <strong>
              {duration >= 60
                ? t('attendance.durationHoursMinutes', {
                    hours: Math.floor(duration / 60),
                    minutes: duration % 60,
                  })
                : t('attendance.durationMinutes', { minutes: duration })}
            </strong>
          </div>
        )}

        {!selected && (
          <Field label={t('attendance.branch')}>
            <Select
              value={branchId}
              onChange={(event) => setManualBranchId(event.target.value)}
              placeholder={t('attendance.branch')}
              options={branches.map((branch) => ({
                value: branch.id,
                label: branch.name,
              }))}
            />
          </Field>
        )}

        {reasonRequired && (
          <Field
            label={t('attendance.reason')}
            hint={t('attendance.reasonHint')}
            error={reasonError ? t('attendance.reasonRequired') : undefined}
          >
            <textarea
              className={styles.textarea}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Field>
        )}

        {actionError && <ErrorState error={actionError} />}
      </Dialog>
    </>
  );
}
